import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LocationsKeys } from "./locations.keys";
import { useApi } from "../../../api/ApiProvider";
import notificationStore from "../../Notification/notificationStore";
import type {
  CreateBusinessDto,
  UpdateBusinessDto,
  CreateAccountDto,
  UpdateAccountDto,
  CreateLocationDto,
  UpdateLocationDto,
} from "../../../api-client";

export type {
  CreateBusinessDto,
  UpdateBusinessDto,
  CreateAccountDto,
  UpdateAccountDto,
  CreateLocationDto,
  UpdateLocationDto,
};

/* ──────── BUSINESS ──────── */

export const useCreateBusiness = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async (dto: CreateBusinessDto) => adminEntities.adminCreateBusiness(dto),
    onSuccess: () => {
      notificationStore.showMessage("Firma erfolgreich erstellt", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.businesses.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Erstellen",
        "error",
      ),
  });
};

export const useUpdateBusiness = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateBusinessDto }) =>
      adminEntities.adminUpdateBusiness(id, dto),
    onSuccess: () => {
      notificationStore.showMessage("Firma aktualisiert", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.businesses.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Aktualisieren",
        "error",
      ),
  });
};

export const useDeleteBusiness = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async (id: string) => adminEntities.adminDeleteBusiness(id),
    onSuccess: () => {
      notificationStore.showMessage("Firma gelöscht", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.businesses.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Löschen",
        "error",
      ),
  });
};

/* ──────── ACCOUNT ──────── */

export const useCreateAccount = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async (dto: CreateAccountDto) => adminEntities.adminCreateAccount(dto),
    onSuccess: () => {
      notificationStore.showMessage("Mandant erfolgreich erstellt", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.accounts.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Erstellen",
        "error",
      ),
  });
};

export const useUpdateAccount = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateAccountDto }) =>
      adminEntities.adminUpdateAccount(id, dto),
    onSuccess: () => {
      notificationStore.showMessage("Mandant aktualisiert", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.accounts.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Aktualisieren",
        "error",
      ),
  });
};

export const useDeleteAccount = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async (id: string) => adminEntities.adminDeleteAccount(id),
    onSuccess: () => {
      notificationStore.showMessage("Mandant gelöscht", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.accounts.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Löschen",
        "error",
      ),
  });
};

export const useUploadAccountLogo = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) =>
      adminEntities.adminUploadAccountLogo(id, file),
    onSuccess: (_data, { id }) => {
      notificationStore.showMessage("Logo erfolgreich hochgeladen", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.accounts.logo(id) });
      qc.invalidateQueries({ queryKey: LocationsKeys.accounts.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Hochladen des Logos",
        "error",
      ),
  });
};

/* ──────── LOCATION ──────── */

export const useCreateLocation = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async (dto: CreateLocationDto) => adminEntities.adminCreateLocation(dto),
    onSuccess: () => {
      notificationStore.showMessage("Standort erfolgreich erstellt", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.locations.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Erstellen",
        "error",
      ),
  });
};

export const useUpdateLocation = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateLocationDto }) =>
      adminEntities.adminUpdateLocation(id, dto),
    onSuccess: () => {
      notificationStore.showMessage("Standort aktualisiert", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.locations.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Aktualisieren",
        "error",
      ),
  });
};

export const useDeleteLocation = () => {
  const qc = useQueryClient();
  const { adminEntities } = useApi();
  return useMutation({
    mutationFn: async (id: string) => adminEntities.adminDeleteLocation(id),
    onSuccess: () => {
      notificationStore.showMessage("Standort gelöscht", "success");
      qc.invalidateQueries({ queryKey: LocationsKeys.locations.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(
        e?.details?.message || e?.message || "Fehler beim Löschen",
        "error",
      ),
  });
};
